import { useEffect, useReducer, useRef, useState } from "react";
import { X, Save, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { get } from "@/api/client";
import { cn } from "@/lib/cn";
import type {
  WorkbookRecord,
  WorkbookSnapshot,
  WorkbookSummary,
} from "@/features/workbook/mock-data";
import type { WorkbookSnapshotDiff } from "@/stores/workbook";

interface SynapsePropertyItem {
  code: string;
  name: string;
  brand: string;
  address: string;
}

export interface WorkbookSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  record: WorkbookRecord;
  workbook: WorkbookSummary;
  activeSnapshot: WorkbookSnapshot | null;
  workbookName: string;
  setWorkbookName: (v: string) => void;
  propertyName: string;
  setPropertyName: (v: string) => void;
  status: string;
  setStatus: (v: string) => void;
  author: string;
  setAuthor: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  hasChanges: boolean;
  saving: boolean;
  validationMessages: string[];
  onSave: () => void;
  selectedSnapshotId: string;
  setSelectedSnapshotId: (v: string) => void;
  rollbackConfirmed: boolean;
  setRollbackConfirmed: (v: boolean) => void;
  rollbackDiff: WorkbookSnapshotDiff[];
  rollingBack: boolean;
  onRollback: () => void;
}

type SidebarTab = "metadata" | "snapshots";

type PropertiesState = { items: SynapsePropertyItem[]; loading: boolean };
type PropertiesAction =
  | { type: "start" }
  | { type: "loaded"; items: SynapsePropertyItem[] }
  | { type: "error" };

function propertiesReducer(_: PropertiesState, action: PropertiesAction): PropertiesState {
  if (action.type === "start") return { items: [], loading: true };
  if (action.type === "loaded") return { items: action.items, loading: false };
  return { items: [], loading: false };
}

export function WorkbookSidebar({
  isOpen,
  onClose,
  record,
  workbook,
  activeSnapshot: _activeSnapshot,
  workbookName,
  setWorkbookName,
  propertyName,
  setPropertyName,
  status,
  setStatus,
  author,
  setAuthor,
  description,
  setDescription,
  hasChanges,
  saving,
  validationMessages,
  onSave,
  selectedSnapshotId,
  setSelectedSnapshotId,
  rollbackConfirmed,
  setRollbackConfirmed,
  rollbackDiff,
  rollingBack,
  onRollback,
}: WorkbookSidebarProps) {
  const [activeTab, setActiveTab] = useState<SidebarTab>("metadata");
  const [propertiesState, dispatchProperties] = useReducer(propertiesReducer, { items: [], loading: false });
  const hasFetchedRef = useRef(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    dispatchProperties({ type: "start" });
    get<SynapsePropertyItem[]>("/api/workbook/synapse-properties/")
      .then((items) => dispatchProperties({ type: "loaded", items }))
      .catch(() => dispatchProperties({ type: "error" }));
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <dialog
        ref={dialogRef}
        aria-label="Workbook metadata"
        onCancel={(e) => { e.preventDefault(); onClose(); }}
        className={cn(
          "fixed inset-y-0 right-0 z-50 m-0 flex w-[480px] flex-col border-0 p-0",
          "border-l border-line bg-surface shadow-2xl",
          "transition-transform duration-200 ease-in-out",
          isOpen ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-line px-5 py-4">
          <span className="text-sm font-semibold text-fg">
            {workbook.workbookName}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-fg-mute hover:bg-surface-tint hover:text-fg"
            aria-label="Close sidebar"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tab switcher */}
        <div className="flex flex-shrink-0 gap-1 border-b border-line px-5 py-2">
          <Button
            type="button"
            size="sm"
            variant={activeTab === "metadata" ? "primary" : "ghost"}
            onClick={() => setActiveTab("metadata")}
          >
            Metadata
          </Button>
          <Button
            type="button"
            size="sm"
            variant={activeTab === "snapshots" ? "primary" : "ghost"}
            onClick={() => setActiveTab("snapshots")}
          >
            Snapshots
          </Button>
        </div>

        <WorkbookSidebarContent
          activeTab={activeTab}
          propertiesLoading={propertiesState.loading}
          properties={propertiesState.items}
          propertyName={propertyName}
          setPropertyName={setPropertyName}
          workbookName={workbookName}
          setWorkbookName={setWorkbookName}
          status={status}
          setStatus={setStatus}
          author={author}
          setAuthor={setAuthor}
          description={description}
          setDescription={setDescription}
          validationMessages={validationMessages}
          record={record}
          selectedSnapshotId={selectedSnapshotId}
          setSelectedSnapshotId={setSelectedSnapshotId}
          rollbackConfirmed={rollbackConfirmed}
          setRollbackConfirmed={setRollbackConfirmed}
          rollbackDiff={rollbackDiff}
          rollingBack={rollingBack}
          onRollback={onRollback}
        />

        {/* Sticky footer — save button */}
        <div className="flex flex-shrink-0 items-center justify-between border-t border-line px-5 py-4">
          <span className="text-xs text-fg-mute">
            {hasChanges ? "Unsaved changes" : "No pending changes"}
          </span>
          <Button
            type="button"
            size="sm"
            onClick={() => void onSave()}
            disabled={!hasChanges || saving}
          >
            <Save size={14} />
            {saving ? "Saving..." : "Save snapshot"}
          </Button>
        </div>
      </dialog>
    </>
  );
}

/* ---------- scrollable body ---------- */

function WorkbookSidebarContent({
  activeTab,
  propertiesLoading,
  properties,
  propertyName, setPropertyName,
  workbookName, setWorkbookName,
  status, setStatus,
  author, setAuthor,
  description, setDescription,
  validationMessages,
  record,
  selectedSnapshotId, setSelectedSnapshotId,
  rollbackConfirmed, setRollbackConfirmed,
  rollbackDiff,
  rollingBack,
  onRollback,
}: {
  activeTab: "metadata" | "snapshots";
  propertiesLoading: boolean;
  properties: SynapsePropertyItem[];
  propertyName: string; setPropertyName: (v: string) => void;
  workbookName: string; setWorkbookName: (v: string) => void;
  status: string; setStatus: (v: string) => void;
  author: string; setAuthor: (v: string) => void;
  description: string; setDescription: (v: string) => void;
  validationMessages: string[];
  record: WorkbookRecord;
  selectedSnapshotId: string; setSelectedSnapshotId: (v: string) => void;
  rollbackConfirmed: boolean; setRollbackConfirmed: (v: boolean) => void;
  rollbackDiff: WorkbookSnapshotDiff[];
  rollingBack: boolean;
  onRollback: () => void;
}) {
  return (
    <div className="flex-1 overflow-y-auto px-5 py-5">
      {activeTab === "metadata" && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sidebar-property">Property</Label>
            {propertiesLoading ? (
              <div className="h-10 animate-pulse rounded-xl bg-surface-tint" />
            ) : (
              <select
                id="sidebar-property"
                aria-label="Property"
                value={propertyName}
                onChange={(e) => setPropertyName(e.target.value)}
                className="h-10 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
              >
                <option value="">— select a property —</option>
                {properties.map((p) => {
                  const label = `${p.name} (${p.code})`;
                  return (
                    <option key={p.code} value={label}>
                      {label}
                    </option>
                  );
                })}
                {propertyName &&
                  !properties.some(
                    (p) => `${p.name} (${p.code})` === propertyName,
                  ) && (
                    <option value={propertyName}>{propertyName}</option>
                  )}
              </select>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sidebar-workbook-name">Workbook name</Label>
            <Input
              id="sidebar-workbook-name"
              value={workbookName}
              onChange={(e) => setWorkbookName(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sidebar-status">Status</Label>
            <Input
              id="sidebar-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sidebar-author">Author</Label>
            <Input
              id="sidebar-author"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sidebar-description">Description</Label>
            <textarea
              id="sidebar-description"
              aria-label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[110px] rounded-xl border border-line bg-surface px-3.5 py-2.5 text-sm text-fg placeholder:text-fg-mute focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
            />
          </div>

          {validationMessages.length > 0 && (
            <div
              role="alert"
              className="rounded-xl border border-danger/35 bg-danger/10 px-3.5 py-3 text-xs text-danger"
            >
              <p className="m-0 font-semibold">Validation issues</p>
              <ul className="mb-0 mt-2 list-disc pl-4">
                {validationMessages.map((msg) => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {activeTab === "snapshots" && (
        <div className="flex flex-col gap-4">
          <div className="text-xs text-fg-mute">
            Active snapshot:{" "}
            <span className="font-mono text-fg">
              {record.activeSnapshotId}
            </span>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sidebar-rollback-snapshot">Rollback target</Label>
            <select
              id="sidebar-rollback-snapshot"
              aria-label="Rollback target"
              value={selectedSnapshotId}
              onChange={(e) => {
                setSelectedSnapshotId(e.target.value);
                setRollbackConfirmed(false);
              }}
              className="h-10 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
            >
              {[...record.snapshots].reverse().map((snap) => (
                <option key={snap.id} value={snap.id}>
                  {snap.id} —{" "}
                  {snap.createdAt.slice(0, 16).replace("T", " ")} —{" "}
                  {snap.reason}
                </option>
              ))}
            </select>
          </div>

          <div className="rounded-xl border border-line bg-surface px-3.5 py-3 text-xs text-fg-dim">
            <div className="mb-2 font-semibold text-fg">Rollback preview</div>
            {selectedSnapshotId === record.activeSnapshotId ? (
              <div>Selected snapshot is already the active revision.</div>
            ) : rollbackDiff.length === 0 ? (
              <div>
                No metadata differences between active and selected snapshots.
              </div>
            ) : (
              <ul className="m-0 list-disc pl-4">
                {rollbackDiff.map((entry) => (
                  <li key={entry.field}>
                    <span className="font-semibold text-fg">{entry.field}:</span>{" "}
                    {entry.from}
                    <span className="mx-1 opacity-70">→</span>
                    {entry.to}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <label className="flex items-center gap-2 text-xs text-fg-dim">
            <input
              type="checkbox"
              checked={rollbackConfirmed}
              onChange={(e) => setRollbackConfirmed(e.target.checked)}
              className="h-4 w-4 rounded border border-line"
            />
            I understand rollback creates a new active snapshot event.
          </label>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void onRollback()}
            disabled={
              rollingBack ||
              selectedSnapshotId === record.activeSnapshotId ||
              !rollbackConfirmed
            }
          >
            <RotateCcw size={14} />
            {rollingBack ? "Rolling back..." : "Rollback to selected"}
          </Button>

          <div className="rounded-xl border border-line bg-surface-tint/40 px-3.5 py-3 text-xs text-fg-dim">
            Rollback creates a new snapshot event instead of mutating history,
            preserving full traceability.
          </div>
        </div>
      )}
    </div>
  );
}
